import { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, Chip, CircularProgress, Collapse } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowUpwardIcon   from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon    from '@mui/icons-material/UnfoldMore';
import { getVulnsByDevice } from '../api/tenable';

const SEV_COLOR = { critical: '#f44336', high: '#ff9800', medium: '#ffeb3b', low: '#4caf50', info: '#607d8b' };
const SEV_ORDER = ['critical', 'high', 'medium', 'low'];

function SevBadge({ sev, count }) {
  if (!count) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
      <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: SEV_COLOR[sev] }} />
      <Typography sx={{ color: SEV_COLOR[sev], fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>{count}</Typography>
    </Box>
  );
}

function VulnSubRow({ v }) {
  const color = SEV_COLOR[v.severity] || '#607d8b';
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 0,
      borderBottom: '1px solid #111827',
      '&:last-child': { borderBottom: 'none' },
      '&:hover': { backgroundColor: '#0a1520' },
    }}>
      <Box sx={{ width: 110, flexShrink: 0, px: 2, py: '8px' }}>
        <Typography sx={{ color: '#4a5a70', fontSize: 11, fontFamily: 'monospace' }}>
          {v.plugin_id || '—'}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, px: 1, py: '8px' }}>
        <Typography sx={{ color: '#a0b4c8', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {v.title}
        </Typography>
        {v.cve_id && (
          <Typography sx={{ color: '#4a5a70', fontSize: 11, mt: 0.25 }}>{v.cve_id}</Typography>
        )}
      </Box>
      <Box sx={{ width: 90, flexShrink: 0, px: 1, py: '8px' }}>
        <Chip label={v.severity} size="small" sx={{ backgroundColor: `${color}22`, color, fontSize: 10, height: 18, textTransform: 'capitalize' }} />
      </Box>
      <Box sx={{ width: 60, flexShrink: 0, px: 1, py: '8px', textAlign: 'right' }}>
        <Typography sx={{ color, fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
          {v.cvss_score ?? '—'}
        </Typography>
      </Box>
      <Box sx={{ width: 60, flexShrink: 0, pr: 2, py: '8px', textAlign: 'right' }}>
        <Typography sx={{ color: '#4a5a70', fontSize: 11 }}>
          {v.last_seen ? new Date(v.last_seen).toLocaleDateString() : '—'}
        </Typography>
      </Box>
    </Box>
  );
}

function DeviceRow({ device }) {
  const [open, setOpen] = useState(false);
  const worstSev = SEV_ORDER.find(s => device.counts[s] > 0);
  const accentColor = SEV_COLOR[worstSev] || '#607d8b';

  return (
    <>
      {/* Device row */}
      <Box
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'flex', alignItems: 'center',
          borderBottom: open ? 'none' : '1px solid #1a2535',
          cursor: 'pointer',
          '&:hover': { backgroundColor: '#0d1825' },
          transition: 'background 0.1s',
        }}
      >
        {/* Expand arrow */}
        <Box sx={{ width: 40, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', py: '14px' }}>
          {open
            ? <KeyboardArrowDownIcon sx={{ color: '#00bcd4', fontSize: 18 }} />
            : <KeyboardArrowRightIcon sx={{ color: '#4a5a70', fontSize: 18 }} />}
        </Box>

        {/* IP */}
        <Box sx={{ width: 140, flexShrink: 0, py: '14px', pr: 1 }}>
          <Typography sx={{ color: '#c0cfe0', fontFamily: 'monospace', fontSize: 13, fontWeight: 700 }}>
            {device.ip || '—'}
          </Typography>
        </Box>

        {/* Hostname */}
        <Box sx={{ width: 200, flexShrink: 0, py: '14px', px: 1 }}>
          <Typography sx={{ color: '#a0b4c8', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {device.hostname || '—'}
          </Typography>
        </Box>

        {/* OS */}
        <Box sx={{ flex: 1, minWidth: 0, py: '14px', px: 1 }}>
          <Typography sx={{ color: '#7a8fa6', fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {device.os || '—'}
          </Typography>
        </Box>

        {/* Severity counts */}
        <Box sx={{ width: 160, flexShrink: 0, display: 'flex', gap: 2, py: '14px', px: 1 }}>
          {SEV_ORDER.map(s => <SevBadge key={s} sev={s} count={device.counts[s]} />)}
        </Box>

        {/* Total */}
        <Box sx={{ width: 70, flexShrink: 0, py: '14px', pr: 2, textAlign: 'right' }}>
          <Typography sx={{ color: accentColor, fontSize: 14, fontWeight: 700, fontFamily: 'monospace' }}>
            {device.total}
          </Typography>
        </Box>
      </Box>

      {/* Expanded vuln sub-table */}
      <Collapse in={open} unmountOnExit>
        <Box sx={{ backgroundColor: '#070c14', borderBottom: '1px solid #1a2535', borderLeft: `3px solid ${accentColor}` }}>
          {/* Sub-header */}
          <Box sx={{ display: 'flex', px: 0, py: '6px', borderBottom: '1px solid #1a2535' }}>
            <Box sx={{ width: 110, px: 2 }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Plugin</Typography>
            </Box>
            <Box sx={{ flex: 1, px: 1 }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Title / CVE</Typography>
            </Box>
            <Box sx={{ width: 90, px: 1 }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Severity</Typography>
            </Box>
            <Box sx={{ width: 60, px: 1, textAlign: 'right' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>CVSS</Typography>
            </Box>
            <Box sx={{ width: 60, pr: 2, textAlign: 'right' }}>
              <Typography sx={{ color: '#4a5a70', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>Seen</Typography>
            </Box>
          </Box>
          {device.vulns.map(v => <VulnSubRow key={v.id} v={v} />)}
        </Box>
      </Collapse>
    </>
  );
}

function ColHeader({ label, sortKey, sortBy, sortDir, onSort, width, flex, align = 'left' }) {
  const active = sortBy === sortKey;
  const Icon = active ? (sortDir === 'asc' ? ArrowUpwardIcon : ArrowDownwardIcon) : UnfoldMoreIcon;
  return (
    <Box
      onClick={() => sortKey && onSort(sortKey)}
      sx={{
        width, flex,
        display: 'flex', alignItems: 'center', gap: 0.5,
        justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
        cursor: sortKey ? 'pointer' : 'default',
        color: active ? '#00bcd4' : '#7a8fa6',
        fontSize: 11, fontWeight: active ? 700 : 500,
        textTransform: 'uppercase', letterSpacing: 0.5,
        userSelect: 'none',
        '&:hover': { color: sortKey ? '#00bcd4' : '#7a8fa6' },
      }}
    >
      {align === 'right' && sortKey && <Icon sx={{ fontSize: 13 }} />}
      {label}
      {align !== 'right' && sortKey && <Icon sx={{ fontSize: 13 }} />}
    </Box>
  );
}

export default function VulnDeviceView() {
  const [devices,  setDevices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [osFilter, setOsFilter] = useState('all');
  const [sortBy,   setSortBy]   = useState('critical');
  const [sortDir,  setSortDir]  = useState('desc');

  useEffect(() => {
    getVulnsByDevice()
      .then(setDevices)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const osOptions = useMemo(() => {
    const seen = new Set();
    const out  = [];
    for (const d of devices) {
      const os = d.os || 'Unknown';
      if (!seen.has(os)) { seen.add(os); out.push(os); }
    }
    return out.sort();
  }, [devices]);

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir(key === 'ip' || key === 'hostname' || key === 'os' ? 'asc' : 'desc'); }
  };

  const displayed = useMemo(() => {
    const list = osFilter === 'all'
      ? devices
      : devices.filter(d => (d.os || 'Unknown') === osFilter);

    return [...list].sort((a, b) => {
      let av, bv;
      if (['critical', 'high', 'medium', 'low'].includes(sortBy)) {
        av = a.counts[sortBy] ?? 0;
        bv = b.counts[sortBy] ?? 0;
      } else if (sortBy === 'total') {
        av = a.total; bv = b.total;
      } else {
        av = (a[sortBy] || '').toString().toLowerCase();
        bv = (b[sortBy] || '').toString().toLowerCase();
      }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });
  }, [devices, osFilter, sortBy, sortDir]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress size={32} sx={{ color: '#00bcd4' }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* OS filter */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {['all', ...osOptions].map(os => {
          const active = osFilter === os;
          return (
            <Chip
              key={os}
              label={os === 'all' ? 'All OS' : os}
              size="small"
              onClick={() => setOsFilter(os)}
              sx={{
                backgroundColor: active ? '#00bcd422' : '#111827',
                color: active ? '#00bcd4' : '#7a8fa6',
                border: `1px solid ${active ? '#00bcd4' : '#1e3a5f'}`,
                fontWeight: active ? 700 : 400,
                fontSize: 12,
                cursor: 'pointer',
                '&:hover': { backgroundColor: '#00bcd411', color: '#00bcd4' },
              }}
            />
          );
        })}
        <Typography sx={{ color: '#4a5a70', fontSize: 12, ml: 1, alignSelf: 'center' }}>
          {displayed.length} device{displayed.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {/* Table */}
      <Paper sx={{ backgroundColor: '#111827', border: '1px solid #1e3a5f', borderRadius: 2, overflow: 'hidden' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', p: '10px 0', borderBottom: '2px solid #1e3a5f', backgroundColor: '#0d1825' }}>
          <Box sx={{ width: 40, flexShrink: 0 }} />
          <ColHeader label="IP Address"  sortKey="ip"       sortBy={sortBy} sortDir={sortDir} onSort={handleSort} width={140} />
          <ColHeader label="Device Name" sortKey="hostname" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} width={200} />
          <ColHeader label="OS"          sortKey="os"       sortBy={sortBy} sortDir={sortDir} onSort={handleSort} flex={1} />
          <Box sx={{ width: 160, flexShrink: 0, display: 'flex', gap: 2, px: 1 }}>
            {SEV_ORDER.map(s => (
              <ColHeader key={s} label={s.charAt(0).toUpperCase() + s.slice(1)} sortKey={s} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            ))}
          </Box>
          <ColHeader label="Total" sortKey="total" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} width={70} align="right" />
        </Box>

        {/* Rows */}
        {displayed.length === 0 ? (
          <Typography sx={{ color: '#4a5a70', textAlign: 'center', p: 5, fontStyle: 'italic' }}>
            No devices with open vulnerabilities.
          </Typography>
        ) : (
          displayed.map(d => <DeviceRow key={d.asset_id} device={d} />)
        )}
      </Paper>
    </Box>
  );
}
